import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { langs } from '@seenaly/shared';
import { usePrefs } from '@/app/prefs';
import { useAuth } from '@/app/auth';
import { errorMessage } from '@/lib/errorMessage';
import { Button, Icon, LabeledInput, Segmented } from '@/components/ui';
import { loginWithPasskey, passkeysSupported } from '@/features/settings/passkeyQueries';

/**
 * Sign-in screen, following the prototype's login layout: radial accent wash,
 * logo, card, and a language switcher below so staff can pick Lao or English
 * before they are signed in.
 */
export function LoginPage() {
  const { t, lang, setLang } = usePrefs();
  const { status, login, adoptSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canUsePasskey = passkeysSupported();

  if (status === 'authenticated') {
    // Return the user to the page that bounced them here, defaulting home.
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  function goHome() {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    void navigate(from, { replace: true });
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login({ username: username.trim(), password });
      goHome();
    } catch (err) {
      setError(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  async function onPasskey() {
    setError(null);
    setBusy(true);
    try {
      adoptSession(await loginWithPasskey());
      goHome();
    } catch (err) {
      // Cancelling the platform prompt throws too — that is a decision, not a
      // failure, so it leaves the form untouched rather than showing an error.
      if (
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'AbortError')
      ) {
        return;
      }
      setError(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={styles.page}>
      <div style={styles.column}>
        <img src="/logo.png" alt="" style={styles.logo} />

        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 24, margin: '0 0 6px' }}>{t.login.title}</h1>
          <p className="sn-muted" style={{ margin: 0, fontSize: 14 }}>
            {t.login.subtitle}
          </p>
        </div>

        <form className="card" style={styles.card} onSubmit={onSubmit}>
          <LabeledInput
            label={t.login.username}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
          <LabeledInput
            label={t.login.password}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          {error && (
            // aria-live so the failure is announced, not just repainted.
            <div className="field-error" role="alert">
              {error}
            </div>
          )}

          <Button type="submit" variant="primary" block disabled={busy}>
            {busy ? t.common.loading : t.btn.signIn}
          </Button>

          {canUsePasskey && (
            <>
              <div style={styles.divider}>
                <span className="sn-muted" style={{ fontSize: 12 }}>
                  {t.login.or}
                </span>
              </div>
              <Button type="button" block disabled={busy} onClick={() => void onPasskey()}>
                <Icon name="key" size={16} /> {t.login.usePasskey}
              </Button>
              <p className="field-hint" style={{ margin: 0, textAlign: 'center' }}>
                {t.login.passkeyHint}
              </p>
            </>
          )}
        </form>

        <Segmented
          value={lang}
          onChange={setLang}
          options={langs.map((value) => ({ value, label: value === 'la' ? 'ລາວ' : 'English' }))}
          aria-label={t.settings.language}
        />
      </div>
    </main>
  );
}

const styles = {
  page: {
    width: '100%',
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: 24,
    background: 'radial-gradient(120% 90% at 15% 0%, var(--color-accent-100) 0%, var(--sn-bg) 55%)',
  },
  column: {
    width: '100%',
    maxWidth: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
  },
  logo: { width: 92, height: 92, objectFit: 'contain' },
  card: { width: '100%', padding: 22, gap: 14 },
  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderTop: '1px solid var(--sn-border)',
    marginTop: 2,
    paddingTop: 12,
  },
} satisfies Record<string, React.CSSProperties>;
